import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CONTACTS, CURRENT_USER, loadConversations, saveConversations } from './chatData';
import ChatSidebar from './ChatSidebar';
import ChatWindow from './ChatWindow';
import './ChatApp.css';

function ChatApp() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState(loadConversations);
  const [activeChat, setActiveChat] = useState('user2');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSendMessage = useCallback((contactId, text, replyTo) => {
    const newMsg = {
      id: Date.now().toString(),
      senderId: CURRENT_USER.id,
      text,
      timestamp: Date.now(),
      status: 'sent',
      replyTo: replyTo || null,
    };

    setConversations((prev) => {
      const conv = prev[contactId] || { messages: [], typing: false, unread: 0 };
      const updated = {
        ...prev,
        [contactId]: { ...conv, messages: [...conv.messages, newMsg] },
      };
      saveConversations(updated);
      return updated;
    });

    // Simulate delivered
    setTimeout(() => {
      setConversations((prev) => {
        const conv = prev[contactId];
        if (!conv) return prev;
        const updated = {
          ...prev,
          [contactId]: {
            ...conv,
            messages: conv.messages.map((m) =>
              m.id === newMsg.id ? { ...m, status: 'delivered' } : m
            ),
          },
        };
        saveConversations(updated);
        return updated;
      });
    }, 800);

    // Simulate typing
    setTimeout(() => {
      setConversations((prev) => {
        const conv = prev[contactId];
        if (!conv) return prev;
        return { ...prev, [contactId]: { ...conv, typing: true } };
      });
    }, 1500);

    // Simulate auto-reply
    setTimeout(() => {
      const { getAutoReply } = require('./chatData');
      const replyMsg = {
        id: (Date.now() + 1).toString(),
        senderId: contactId,
        text: getAutoReply(contactId),
        timestamp: Date.now(),
        status: 'read',
      };

      setConversations((prev) => {
        const conv = prev[contactId];
        if (!conv) return prev;
        const updated = {
          ...prev,
          [contactId]: {
            ...conv,
            typing: false,
            messages: [
              ...conv.messages.map((m) =>
                m.id === newMsg.id ? { ...m, status: 'read' } : m
              ),
              replyMsg,
            ],
            unread: activeChat !== contactId ? (conv.unread || 0) + 1 : 0,
          },
        };
        saveConversations(updated);
        return updated;
      });
    }, 2500 + Math.random() * 2000);
  }, [activeChat]);

  const handleSelectChat = useCallback((contactId) => {
    setActiveChat(contactId);
    setSidebarOpen(false);
    // Mark as read
    setConversations((prev) => {
      const conv = prev[contactId];
      if (!conv || conv.unread === 0) return prev;
      const updated = { ...prev, [contactId]: { ...conv, unread: 0 } };
      saveConversations(updated);
      return updated;
    });
  }, []);

  const handleDeleteConversation = useCallback((contactId) => {
    setConversations((prev) => {
      const updated = {
        ...prev,
        [contactId]: { messages: [], typing: false, unread: 0 },
      };
      saveConversations(updated);
      return updated;
    });
  }, []);

  const handleResetDemo = useCallback(() => {
    localStorage.removeItem('demohub_chat');
    const fresh = loadConversations();
    setConversations(fresh);
  }, []);

  const activeContact = CONTACTS.find((c) => c.id === activeChat);
  const activeConversation = conversations[activeChat] || { messages: [], typing: false, unread: 0 };

  const totalUnread = Object.values(conversations).reduce((sum, c) => sum + (c.unread || 0), 0);

  return (
    <div className="chat-app">
      <div className="chat-topbar">
        <button className="chat-back-btn" onClick={() => navigate('/')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="chat-app-title">
          Chat
          {totalUnread > 0 && <span className="chat-app-badge">{totalUnread}</span>}
        </h1>
        <button className="chat-reset-btn" onClick={handleResetDemo}>↻ Reset</button>
        <button
          className="chat-sidebar-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </div>

      <div className="chat-body">
        <ChatSidebar
          contacts={CONTACTS}
          conversations={conversations}
          activeChat={activeChat}
          onSelect={handleSelectChat}
          onDelete={handleDeleteConversation}
          isOpen={sidebarOpen}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        <ChatWindow
          contact={activeContact}
          conversation={activeConversation}
          currentUser={CURRENT_USER}
          onSend={(text, replyTo) => handleSendMessage(activeChat, text, replyTo)}
        />
      </div>
    </div>
  );
}

export default ChatApp;
